import React, { useState, useEffect } from 'react';
import { 
  UserPlus, Plus, LogOut, Mail, X, XCircle, 
  Trash2, AlertCircle, History
} from 'lucide-react';
import { useAuth } from '../../../App'; 
import ConfirmationModal from './ConfirmationModal';
import AddMemberForm from './AddMemberForm';
import AddSubscriptionForm from './AddSubscriptionForm';
import MemberList from './MemberList';
import TransactionList from './TransactionList';
import { useTranslation } from '../../../contexts/LanguageContext';

const SharingSpaceDetails = ({ 
  space, 
  onDelete, 
  onRemoveSpaceSubscription,
  onRemovePersonalSubscription,
  onRemoveMember,
  onUpdateBalance,
  onInviteNewMembers,  
  onAddSubscriptions,  
  onCancelInvitation,
  friends = [],
  userSubscriptions = [],
  sharingSpaces = [],
  setSharingSpaces,
  setActiveSpace,
  activeSpace
}) => {
  const { t } = useTranslation();
  const { user, httpClient } = useAuth(); // Get user and httpClient from auth context
  
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showAddSubscriptionForm, setShowAddSubscriptionForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [otherSpacesModal, setOtherSpacesModal] = useState({
    open: false,
    merchant_id: null,
    spaces: []
  });
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(true);
  
  const [confirmRemoveSubscription, setConfirmRemoveSubscription] = useState({
    open: false,
    plan: null
  });
  
  const currentUserId = parseInt(user?.userId, 10);
  const isAdmin = space?.admin_id === currentUserId;
  
  const members = space?.members || [];
  const plan_list = space?.plan_list || [];
  
  const acceptedMembers = members.filter(m => m.request_status === 'accepted');
  const pendingInvitations = members.filter(m => m.request_status === 'pending');

  const calculateCosts = () => {
    let monthlyCost = 0;
    let yearlyCost = 0;
    
    plan_list.forEach(plan => {
      const price = parseFloat(plan.price || 0);
      
      if (plan.billing_interval === 'monthly') {
        monthlyCost += price;
        yearlyCost += price * 12;
      } else if (plan.billing_interval === 'yearly') {
        monthlyCost += price / 12;
        yearlyCost += price;
      } else {
        monthlyCost += price;
        yearlyCost += price * 12;
      }
    });
    
    return {
      monthly: monthlyCost,
      yearly: yearlyCost,
      perPerson: acceptedMembers.length > 0 ? (monthlyCost / acceptedMembers.length) : 0
    };
  };
  
  const costs = calculateCosts();

  const fetchTransactions = async () => {
    try {
      if (!httpClient || !space) return;
      
      const response = await httpClient.request(`/api/sharing-space/${space.id}/transactions`);
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const createTransaction = async (memberId, amount, description) => {
    if (!httpClient) return false;
    
    setIsProcessing(true);
    try {
      const memberIdNum = Number(memberId);
      const response = await httpClient.request(
        `/api/sharing-space/${space.id}/transaction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            member_id: memberIdNum,
            type: 'payment',
            amount,
            description
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sharesubs.errors.failedToCreateTransaction'));
      }
  
      const transactionData = await response.json();
      await fetchTransactions();
      
      const updateMemberBalances = (members) => {
        return members.map(m => {
          if (Number(m.member_id) === memberIdNum) {
            const currentBalance = Number(m.balance) || 0;
            const newBalance = currentBalance + amount;
            return { ...m, balance: newBalance };
          } else if (Number(m.member_id) === Number(space.admin_id)) {
            const currentBalance = Number(m.balance) || 0;
            const newBalance = currentBalance - amount;
            return { ...m, balance: newBalance };
          }
          return m;
        });
      };
      
      if (setSharingSpaces) {
        setSharingSpaces(prev => prev.map(s => {
          if (s.id === space.id) {
            const updatedMembers = updateMemberBalances(s.members);
            return { ...s, members: updatedMembers };
          }
          return s;
        }));
      }
      
      if (setActiveSpace) {
        setActiveSpace(prev => {
          if (prev && prev.id === space.id) {
            const updatedMembers = updateMemberBalances(prev.members);
            return { ...prev, members: updatedMembers };
          }
          return prev;
        });
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
      return true;
    } catch (error) {
      console.error('Error creating transaction:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToCreateTransaction')}: ${error.message}`);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (space) {
      fetchTransactions();
    }
  }, [space, httpClient]);

  useEffect(() => {
    if (isAdmin || !space || !userSubscriptions || !currentUserId) return;
    
    const userSpaces = sharingSpaces.filter(s => 
      s.members.some(m => 
        m.member_id === currentUserId && 
        m.request_status === 'accepted'
      )
    );
    
    const userConflicts = plan_list.map(plan => {
      const personalConflict = userSubscriptions.some(
        sub => sub.merchant_id === plan.merchant_id
      );
      
      const spaceConflicts = userSpaces
        .filter(s => s.id !== space.id)
        .filter(s => 
          s.plan_list.some(p => p.merchant_id === plan.merchant_id)
        );
      
      return {
        merchant_id: plan.merchant_id,
        subscription_name: plan.plan_name,
        conflict_type: personalConflict ? 
          (spaceConflicts.length > 0 ? 'both' : 'personal') : 
          spaceConflicts.length > 0 ? 'space' : null,
        space_conflicts: spaceConflicts
      };
    }).filter(conflict => conflict.conflict_type);
    
    setConflicts(userConflicts);
  }, [space, userSubscriptions, sharingSpaces, isAdmin, currentUserId]);

  const handleRemoveSubscription = (plan) => {
    const identifier = plan.merchant_id;
    setConfirmRemoveSubscription({
      open: true,
      plan: { ...plan, identifier }
    });
  };
  
  const confirmRemoveAction = () => {
    if (confirmRemoveSubscription.plan) {
      onRemoveSpaceSubscription(space, confirmRemoveSubscription.plan.identifier);
      setConfirmRemoveSubscription({ open: false, plan: null });
    }
  };

  const handleInviteMembers = async (membersToInvite) => {
    setIsProcessing(true);
    setErrorMessage('');
    
    try {
      await onInviteNewMembers(space, membersToInvite);
      setShowAddMemberForm(false);
    } catch (error) {
      console.error('Error inviting members:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToInviteMembers')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddSubscriptions = async (subscriptionIds) => {
    setIsProcessing(true);
    setErrorMessage('');
    
    try {
      await onAddSubscriptions({ ...space, id: space._id || space.id }, subscriptionIds);
      setShowAddSubscriptionForm(false);
    } catch (error) {
      console.error('Error adding subscriptions:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToAddSubscriptions')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResolveConflict = async (merchant_id) => {
    setIsProcessing(true);
    try {
      await onRemovePersonalSubscription(merchant_id);
      setConflicts(prev => prev.filter(c => c.merchant_id !== merchant_id));
    } catch (error) {
      console.error('Error resolving conflict:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToResolveConflict')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleLeaveOtherSpace = async (merchant_id) => {
    setIsProcessing(true);
    try {
      const conflict = conflicts.find(c => c.merchant_id === merchant_id);
      if (!conflict) {
        throw new Error(t('sharesubs.errors.conflictNotFound'));
      }
      
      if (conflict.space_conflicts.length === 1) {
        const spaceToLeave = conflict.space_conflicts[0];
        if (window.confirm(`${t('sharesubs.modals.leaveSpace.description')} "${spaceToLeave.name}"?`)) {
          await onRemoveMember(spaceToLeave, currentUserId);
          setConflicts(prev => prev.filter(c => c.merchant_id !== merchant_id));
        }
      } else if (conflict.space_conflicts.length > 1) {
        setOtherSpacesModal({
          open: true,
          merchant_id,
          spaces: conflict.space_conflicts
        });
      } else {
        throw new Error(t('sharesubs.errors.noConflictingSpaces'));
      }
    } catch (error) {
      console.error('Error leaving other space:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToLeaveSpace')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeaveSelectedSpace = async (spaceToLeave) => {
    setOtherSpacesModal({ open: false, merchant_id: null, spaces: [] });
    
    setIsProcessing(true);
    try {
      await onRemoveMember(spaceToLeave, currentUserId);
      
      setConflicts(prev => prev.map(conflict => {
        if (conflict.merchant_id === otherSpacesModal.merchant_id) {
          return {
            ...conflict,
            space_conflicts: conflict.space_conflicts.filter(s => s.id !== spaceToLeave.id),
            conflict_type: conflict.space_conflicts.length === 1 ? 
              (conflict.conflict_type === 'both' ? 'personal' : null) : 
              conflict.conflict_type
          };
        }
        return conflict;
      }).filter(conflict => 
        conflict.conflict_type !== null && 
        (conflict.space_conflicts.length > 0 || conflict.conflict_type === 'personal')
      ));
    } catch (error) {
      console.error('Error leaving selected space:', error);
      setErrorMessage(`${t('sharesubs.errors.failedToLeaveSpace')}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mt-6 border-t pt-6">
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {errorMessage}
        </div>
      )}
      
      {!isAdmin && conflicts.length > 0 && (
        <div className="mt-4 border border-yellow-300 rounded-lg bg-yellow-50 p-4">
          <div className="flex items-center mb-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            <h3 className="font-semibold text-yellow-800">
              {t('sharesubs.conflictResolution.title')}
            </h3>
            <span className="ml-2 bg-yellow-200 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
              {conflicts.length} {t('sharesubs.conflictResolution.conflicts')}
            </span>
          </div>
          
          <p className="text-sm text-yellow-700 mb-4">
            {t('sharesubs.conflictResolution.description')}
          </p>
          
          <div className="space-y-3">
            {conflicts.map((conflict, index) => (
              <div key={index} className="p-3 bg-yellow-100 rounded-lg">
                <div className="font-medium">{conflict.subscription_name}</div>
                <div className="text-sm text-yellow-700 mb-2">
                  {t('sharesubs.conflictResolution.conflictWith')}: 
                  {conflict.conflict_type === 'personal' && ` ${t('sharesubs.conflictResolution.personalSubscription')}`}
                  {conflict.conflict_type === 'space' && ` ${t('sharesubs.conflictResolution.anotherSpace')}`}
                  {conflict.conflict_type === 'both' && ` ${t('sharesubs.conflictResolution.both')}`}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {conflict.conflict_type !== 'space' && (
                    <button
                      onClick={() => handleResolveConflict(conflict.merchant_id)}
                      disabled={isProcessing}
                      className={`px-3 py-1 text-sm rounded-lg flex items-center ${
                        isProcessing
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {isProcessing ? (
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      {t('sharesubs.actions.removePersonalSubscription')}
                    </button>
                  )}
                  
                  <button
                    onClick={() => onRemoveMember(space, currentUserId)}
                    disabled={isProcessing}
                    className={`px-3 py-1 border rounded-lg flex items-center ${
                        isProcessing
                          ? 'border-gray-300 text-gray-500 cursor-not-allowed'
                          : 'border-red-600 text-red-600 hover:bg-red-50'
                      }`}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('sharesubs.actions.leaveSpace')}
                  </button>
                  
                  {conflict.conflict_type !== 'personal' && (
                    <button
                      onClick={() => handleLeaveOtherSpace(conflict.merchant_id)}
                      disabled={isProcessing}
                      className={`px-3 py-1 border rounded-lg flex items-center ${
                        isProcessing
                          ? 'border-gray-300 text-gray-500 cursor-not-allowed'
                          : 'border-blue-600 text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('sharesubs.actions.leaveOtherSpace')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <ConfirmationModal
        isOpen={confirmRemoveSubscription.open}
        onCancel={() => setConfirmRemoveSubscription({ open: false, plan: null })}
        onConfirm={confirmRemoveAction}
        title={t('sharesubs.modals.removeSubscription.title')}
        message={`${t('sharesubs.modals.removeSubscription.description')} "${confirmRemoveSubscription.plan?.plan_name}"?`}
        confirmText={t('sharesubs.modals.removeSubscription.confirmText')}
        confirmColor="#e53e3e"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div>
          <MemberList
            members={space.members}
            space={space}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            onRemoveMember={onRemoveMember}
            onCreateTransaction={createTransaction}
            monthlyCost={costs.monthly}
            key={`member-list-${space.id}-${JSON.stringify(space.members.map(m => ({id: m.member_id, balance: m.balance})))}`}
          />
          
          {isAdmin && (
            <div className="mt-3">
              {showAddMemberForm ? (
                <AddMemberForm
                  friends={friends}
                  existingMemberEmails={members.map(m => m.member_email)}
                  onInvite={handleInviteMembers}
                  onCancel={() => setShowAddMemberForm(false)}
                  isProcessing={isProcessing}
                />
              ) : (
                <button 
                  onClick={() => setShowAddMemberForm(true)}
                  className="w-full py-2 bg-[#253985] bg-opacity-10 text-[#253985] rounded-lg hover:bg-[#253985] hover:text-white flex items-center justify-center transition-colors"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('sharesubs.actions.addMember')}
                </button>
              )}
            </div>
          )}
          
          {pendingInvitations.length > 0 && (
            <div className="mb-6 mt-6">
              <h4 className="font-bold text-lg text-gray-800 mb-3 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-[#253985]" /> 
                {t('sharesubs.pendingInvitations.title')}
              </h4>
              <div className="border border-gray-200 rounded-lg divide-y">
                {pendingInvitations.map((invite, index) => (
                  <div key={index} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <div className="font-medium">{invite.member_email}</div>
                      <div className="text-xs text-gray-500">
                        {t('sharesubs.pendingInvitations.invited')}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {isAdmin && (
                        <button 
                          onClick={() => onCancelInvitation(space, invite.member_email)}
                          className="text-red-500 hover:text-red-700 p-1 transition-colors"
                          title={t('sharesubs.actions.cancelInvitation')}
                          disabled={isProcessing}
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold flex items-center">
                <History className="h-5 w-5 mr-2" />
                {t('sharesubs.transactionHistory.title')}
              </h4>
              <button
                onClick={() => setShowTransactions(!showTransactions)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showTransactions ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showTransactions && (
              <TransactionList 
                transactions={transactions}
                space={space}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
              />
            )}
          </div>
        </div>
        
        <div>
          <div className="mb-6">
            <h4 className="font-bold text-lg text-gray-800 mb-3">
              {t('sharesubs.sharedSubscriptions.title')} ({plan_list.length})
            </h4>
            <div className="border border-gray-200 rounded-lg divide-y">
              {plan_list.map((plan, index) => (
                <div key={index} className="p-3 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center">
                    {plan.logo ? (
                      <img 
                        src={plan.logo} 
                        alt={plan.plan_name}
                        className="w-10 h-10 rounded-lg mr-3 object-contain"
                      />
                    ) : (
                      <div className="bg-gray-200 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-xs font-medium">
                          {plan.plan_name ? plan.plan_name.substring(0, 2).toUpperCase() : 'SU'}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{plan.plan_name}</div>
                      <div className="text-sm text-gray-500">
                        €{plan.price} / {plan.billing_interval}
                      </div>
                    </div>
                  </div>
                  <div>
                    {isAdmin ? (
                      <button 
                        onClick={() => handleRemoveSubscription(plan)}
                        className="text-red-500 hover:text-red-700 p-1 transition-colors"
                        title={t('sharesubs.actions.removeSubscription')}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 inline-block"></span>
                        ) : (
                          <XCircle className="h-5 w-5" />
                        )}
                      </button>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {t('sharesubs.sharedSubscriptions.addedByAdmin')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {isAdmin && (
              <div className="mt-3">
                {showAddSubscriptionForm ? (
                  <AddSubscriptionForm
                    userSubscriptions={userSubscriptions}
                    currentSubscriptions={plan_list}
                    onAdd={handleAddSubscriptions}
                    onCancel={() => setShowAddSubscriptionForm(false)}
                    isProcessing={isProcessing}
                  />
                ) : (
                  <button 
                    onClick={() => setShowAddSubscriptionForm(true)}
                    className="w-full py-2 bg-[#253985] bg-opacity-10 text-[#253985] rounded-lg hover:bg-[#253985] hover:text-white flex items-center justify-center transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('sharesubs.actions.addSubscription')}
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-[#253985] bg-opacity-10 p-4 rounded-lg border border-[#253985] border-opacity-20 mb-6">
            <h4 className="font-bold text-lg text-gray-800 mb-2">
              {t('sharesubs.costSummary.title')}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-sm text-gray-600">
                {t('sharesubs.costSummary.monthlyCost')}:
              </div>
              <div className="text-sm font-bold text-right text-[#253985]">
                €{costs.monthly.toFixed(2)}
              </div>
              
              <div className="text-sm text-gray-600">
                {t('sharesubs.costSummary.yearlyCost')}:
              </div>
              <div className="text-sm font-bold text-right text-[#253985]">
                €{costs.yearly.toFixed(2)}
              </div>
              
              <div className="text-sm text-gray-600">
                {t('sharesubs.costSummary.perPerson')}:
              </div>
              <div className="text-sm font-bold text-right text-[#253985]">
                €{costs.perPerson.toFixed(2)}
              </div>
              
              <div className="text-sm text-gray-600">
                {t('sharesubs.costSummary.yourStatus')}:
              </div>
              <div className="text-sm font-medium text-right">
                <span className={`px-2 py-1 rounded-full ${
                  isAdmin 
                    ? "bg-yellow-100 text-yellow-800" 
                    : "bg-[#253985] bg-opacity-20 text-[#253985]"
                }`}>
                  {isAdmin ? t('sharesubs.members.admin') : t('sharesubs.members.member')}
                </span>
              </div>
            </div>
          </div>
          
          {isAdmin && (
            <div className="border-t pt-4">
              <button 
                onClick={() => {
                  const spaceId = space._id?.$oid || space._id || space.id;
                  onDelete({ ...space, id: spaceId });
                }}
                className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center transition-colors"
                disabled={isProcessing}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('sharesubs.actions.deleteSpace')}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {t('sharesubs.deleteSpace.warning')}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {otherSpacesModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {t('sharesubs.modals.selectSpace.title')}
            </h3>
            <p className="mb-4">
              {t('sharesubs.modals.selectSpace.description')}
            </p>
            
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {otherSpacesModal.spaces.map((space, index) => (
                <div 
                  key={space.id}
                  onClick={() => handleLeaveSelectedSpace(space)}
                  className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <div className="font-medium">{space.name}</div>
                  <div className="text-sm text-gray-500">
                    {space.plan_list.find(p => p.merchant_id === otherSpacesModal.merchant_id)?.plan_name}
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => setOtherSpacesModal({ open: false, merchant_id: null, spaces: [] })}
              className="w-full py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              {t('sharesubs.actions.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharingSpaceDetails;